import type { DesignDocument, DesignNode, ImageNode, SizeMm, TextNode } from '@repo/api-contracts';

/** 도구 막대 정렬 — 가로(왼쪽·가운데·오른쪽)와 세로(위·가운데·아래). */
export type LayerAlignment = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';

export type FlipAxis = 'x' | 'y';

/** 같은 부모 안에서의 자리 — 0 이 맨 아래(배열 앞), count - 1 이 맨 위다(D13). */
export interface SiblingPosition {
  readonly index: number;
  readonly count: number;
}

/** 회전을 반영한 축 정렬 상자의 반폭·반높이. 이미지·텍스트는 중심 피벗이다(DESIGN_COORDINATE_SYSTEM). */
const rotatedHalfExtents = (node: ImageNode | TextNode): { hw: number; hh: number } => {
  const rad = (node.rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    hw: (node.width / 2) * cos + (node.height / 2) * sin,
    hh: (node.width / 2) * sin + (node.height / 2) * cos,
  };
};

/**
 * 인쇄 영역(컴포넌트 mm) 안에서 노드를 정렬한다. 회전한 노드는 화면에 보이는 외곽 상자 기준으로 맞춘다.
 * 한 축만 바꾸고 다른 축·크기·회전은 그대로 둔다.
 */
export const alignInArea = <T extends ImageNode | TextNode>(
  node: T,
  area: SizeMm,
  alignment: LayerAlignment,
): T => {
  const { hw, hh } = rotatedHalfExtents(node);
  const centerX = (() => {
    if (alignment === 'left') return hw;
    if (alignment === 'center') return area.widthMm / 2;
    if (alignment === 'right') return area.widthMm - hw;
    return null;
  })();
  const centerY = (() => {
    if (alignment === 'top') return hh;
    if (alignment === 'middle') return area.heightMm / 2;
    if (alignment === 'bottom') return area.heightMm - hh;
    return null;
  })();
  return {
    ...node,
    x: centerX === null ? node.x : centerX - node.width / 2,
    y: centerY === null ? node.y : centerY - node.height / 2,
  };
};

/** 이미지 좌우(x)·상하(y) 반전 — 텍스트에는 반전이 없다(계약 textNodeSchema). */
export const flipImage = (node: ImageNode, axis: FlipAxis): ImageNode =>
  axis === 'x' ? { ...node, flipX: !node.flipX } : { ...node, flipY: !node.flipY };

/** 처음으로 — 모든 면·컴포넌트의 레이어를 비운다. 상품·색상은 그대로다. */
export const clearDocument = (document: DesignDocument): DesignDocument => ({
  ...document,
  components: [],
});

/** 레이어가 하나라도 있는지(그룹 포함). */
export const hasAnyLayer = (document: DesignDocument): boolean =>
  document.components.some((component) => component.layers.length > 0);

const positionIn = (nodes: readonly DesignNode[], nodeId: string): SiblingPosition | null => {
  const index = nodes.findIndex((node) => node.id === nodeId);
  if (index >= 0) return { index, count: nodes.length };
  for (const node of nodes) {
    if (node.type !== 'group') continue;
    const inner = positionIn(node.children, nodeId);
    if (inner !== null) return inner;
  }
  return null;
};

/** 노드가 같은 부모 안 몇 번째인지 — 앞으로/뒤로 버튼의 활성 여부에 쓴다. 없으면 null. */
export const siblingPosition = (
  document: DesignDocument,
  nodeId: string,
): SiblingPosition | null => {
  for (const component of document.components) {
    const position = positionIn(component.layers, nodeId);
    if (position !== null) return position;
  }
  return null;
};
